"use client";

import { type JSX, useEffect, useMemo } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { type Field } from "@sitecore-content-sdk/nextjs";
import { ComponentProps } from "lib/component-props";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    __sitecoreLastGtmSignature?: string;
  }
}

type TaxonomyLikeItem = {
  fields?: {
    TaxonomyLabel?: { value?: string };
  };
  taxonomyLabel?: { value?: string };
  name?: string;
  displayName?: string;
};

type TaxonomyFieldValue = {
  targetItems?: Array<TaxonomyLikeItem | null | undefined>;
  jsonValue?: {
    value?: Array<TaxonomyLikeItem | null | undefined>;
  };
  value?: Array<TaxonomyLikeItem | null | undefined>;
};

type TaxonomyFieldLike =
  | Array<TaxonomyLikeItem | null | undefined>
  | TaxonomyFieldValue
  | null
  | undefined;

type GtmPageMetadata = {
  solutions: string;
  applications: string;
  markets: string;
  industries: string;
  companies: string;
  country: string;
  pageOwner: string;
  locationRegion: string;
  locationType: string;
  translationOwner: string;
  templateType: string;
};

const getTaxonomyItems = (field: TaxonomyFieldLike): TaxonomyLikeItem[] => {
  if (Array.isArray(field)) {
    return field.filter(Boolean) as TaxonomyLikeItem[];
  }
  return (
    field?.targetItems ??
    field?.jsonValue?.value ??
    field?.value ??
    []
  ).filter(Boolean) as TaxonomyLikeItem[];
};

const getJoinedTaxonomyValues = (field?: TaxonomyFieldLike): string => {
  const items = getTaxonomyItems(field);

  return items
    .map(
      (item) =>
        item?.fields?.TaxonomyLabel?.value ??
        item?.taxonomyLabel?.value ??
        item?.name ??
        item?.displayName ??
        "",
    )
    .filter(Boolean)
    .join(";");
};

const getRouteFieldValue = (field: unknown): string | undefined => {
  if (typeof field === "string") return field;
  if (field && typeof field === "object" && "value" in field) {
    const value = (field as { value?: unknown }).value;
    return typeof value === "string" ? value : undefined;
  }
  return undefined;
};

const serializeForInlineScript = (value: object): string =>
  JSON.stringify(value).replace(/</g, "\\u003c");

export type GtmFields = {
  GTMID?: Field<string>;
};

export type GtmProps = ComponentProps & {
  fields?: GtmFields;
};

export const Default = (props: GtmProps): JSX.Element | null => {
  const { page, fields } = props;
  const pathname = usePathname();
  const route = page?.layout?.sitecore?.route;
  const isPageEditing = page?.mode?.isEditing ?? false;
  const isPartialDesign = route?.templateName === "Partial Design";
  const routeFields = route?.fields as Record<string, unknown> | undefined;

  const gtmId =
    typeof fields?.GTMID?.value === "string" ? fields.GTMID.value.trim() : "";

  //read it from env variables 
  const gtmDataLayerEventName =
    process.env.NEXT_PUBLIC_GTM_DATALAYER_EVENT_NAME?.trim() ?? "ecolabdotcom_gtm_datalayer_push";

  const gtmDataLayer = useMemo<GtmPageMetadata>(
    () => ({
      solutions: getJoinedTaxonomyValues(
        routeFields?.Solutions as TaxonomyFieldLike,
      ),
      applications: getJoinedTaxonomyValues(
        routeFields?.Applications as TaxonomyFieldLike,
      ),
      markets: getJoinedTaxonomyValues(
        routeFields?.Markets as TaxonomyFieldLike,
      ),
      industries: getJoinedTaxonomyValues(
        routeFields?.Industries as TaxonomyFieldLike,
      ),
      companies: getJoinedTaxonomyValues(
        routeFields?.Companies as TaxonomyFieldLike,
      ),
      country: getJoinedTaxonomyValues(
        routeFields?.Countries as TaxonomyFieldLike,
      ),
      pageOwner:
        [
          getRouteFieldValue(routeFields?.pageOwner),
          getRouteFieldValue(routeFields?.PageOwner),
          getJoinedTaxonomyValues(
            routeFields?.PageOwner as TaxonomyFieldLike,
          ),
        ].find((value) => Boolean(value?.trim())) ?? "",
      locationRegion: getJoinedTaxonomyValues(
        routeFields?.Regions as TaxonomyFieldLike,
      ),
      locationType: getJoinedTaxonomyValues(
        routeFields?.LocationType as TaxonomyFieldLike,
      ),
      translationOwner: getJoinedTaxonomyValues(
        routeFields?.TranslationOwner as TaxonomyFieldLike,
      ),
      templateType: route?.templateName ?? "",
    }),
    [routeFields, route?.templateName],
  );

  const serializedGtmDataLayer = useMemo(
    () => serializeForInlineScript(gtmDataLayer),
    [gtmDataLayer],
  );

  useEffect(() => {
    if (!gtmId || isPageEditing || isPartialDesign) {
      return;
    }

    if (!gtmDataLayerEventName) {
      console.error(
        "[GTM] Missing NEXT_PUBLIC_GTM_DATALAYER_EVENT_NAME. " +
          "The Sitecore data-layer event was not pushed.",
      );
      return;
    }

    const pagePath = `${pathname}${window.location.search}`;
    const sitecoreItemId = route?.itemId ?? "";

    const signature = [
      gtmDataLayerEventName,
      pagePath,
      sitecoreItemId,
      serializedGtmDataLayer,
    ].join("|");

    if (window.__sitecoreLastGtmSignature === signature) {
      return;
    }

    // Keep the legacy script-based data-gtm attribute current on SPA routes.
    document
      .getElementById("gtm-data-layer")
      ?.setAttribute("data-gtm", serializedGtmDataLayer);

    window.dataLayer ??= [];
    window.dataLayer.push({
      event: gtmDataLayerEventName,
      ...gtmDataLayer,
      pagePath,
      pageLocation: window.location.href,
      pageTitle: document.title,
      sitecoreItemId,
    });

    window.__sitecoreLastGtmSignature = signature;

  }, [
    gtmId,
    gtmDataLayer,
    gtmDataLayerEventName,
    isPageEditing,
    isPartialDesign,
    pathname,
    route?.itemId,
    serializedGtmDataLayer,
  ]);

  if (isPageEditing && isPartialDesign) {
    return (
      <div className="is-metadata-hint">
        <strong>Metadata:</strong> Google Tag Manager
      </div>
    );
  }

  if (!gtmId || isPageEditing) {
    return null;
  }

  return (
    <>
      {/*
        Preserve the legacy script-based data-gtm attribute. The React effect
        performs the actual initial and SPA dataLayer pushes.
      */}
      <Script
        id="gtm-data-layer"
        strategy="beforeInteractive"
        data-gtm={serializedGtmDataLayer}
      >
        {`window.dataLayer = window.dataLayer || [];`}
      </Script>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){
            w[l]=w[l]||[];
            w[l].push({
              'gtm.start': new Date().getTime(),
              event: 'gtm.js'
            });
            var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),
                dl=l!='dataLayer'?'&l='+l:'';
            j.async=true;
            j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
            f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `}
      </Script>
      <noscript>
        <iframe
          id="noscript-gtm"
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          title="noscript-gtm"
        />
      </noscript>
    </>
  );
};
